import { ReactNode, useState } from 'react'
import clsx from 'clsx'
import { Sparkles, ChevronRight, AlertCircle, Lightbulb, TrendingUp, Info, Brain, Database, HelpCircle } from 'lucide-react'

type InsightType = 'analysis' | 'warning' | 'recommendation' | 'prediction'
type ModelType = 'foundation' | 'base' | 'per_asset' | 'ensemble' | 'rule_based'

interface AIInsightPanelProps {
  type?: InsightType
  title?: string
  content: string | ReactNode
  confidence?: number
  timestamp?: string
  actions?: ReactNode
  onExpand?: () => void
  className?: string
  // Enhanced properties for SME/Technician context
  modelType?: ModelType  // Which AI model generated this insight
  dataSource?: string  // What data was used (e.g., "Last 24h sensor data")
  assetId?: string  // Related asset ID
  showModelInfo?: boolean  // Show detailed model information
  helpText?: string  // Quick explanation for technicians
}

const typeConfig = {
  analysis: {
    icon: Sparkles,
    border: 'border-primary-500',
    iconColor: 'text-primary-400',
    bg: 'bg-primary-500/5',
    label: 'Analysis'
  },
  warning: {
    icon: AlertCircle,
    border: 'border-status-warning',
    iconColor: 'text-status-warning',
    bg: 'bg-status-warning/5',
    label: 'Warning'
  },
  recommendation: {
    icon: Lightbulb,
    border: 'border-accent-cyan',
    iconColor: 'text-accent-cyan',
    bg: 'bg-accent-cyan/5',
    label: 'Recommendation'
  },
  prediction: {
    icon: TrendingUp,
    border: 'border-accent-purple',
    iconColor: 'text-accent-purple',
    bg: 'bg-accent-purple/5',
    label: 'Prediction'
  }
}

const modelInfo = {
  foundation: {
    name: 'Foundation Model',
    description: 'General-purpose ML model trained on diverse industrial equipment data. Provides baseline predictions for any asset type.',
    accuracy: '85-90%'
  },
  base: {
    name: 'Base Model',
    description: 'Specialized model trained on Ariel compressor fleet data. Better accuracy for gas compression equipment.',
    accuracy: '88-93%'
  },
  per_asset: {
    name: 'Per-Asset Model',
    description: 'Custom model trained specifically on this asset\'s historical data. Highest accuracy for familiar operating patterns.',
    accuracy: '92-96%'
  },
  ensemble: {
    name: 'Ensemble Model',
    description: 'Combines predictions from multiple models weighted by confidence. Most reliable for critical decisions.',
    accuracy: '90-95%'
  },
  rule_based: {
    name: 'Rule-Based System',
    description: 'Expert-defined thresholds and rules from domain SMEs. Deterministic and explainable.',
    accuracy: 'N/A (Deterministic)'
  }
}

// Confidence level explanation for technicians
const getConfidenceExplanation = (confidence: number): string => {
  if (confidence >= 0.9) return 'Very High - AI is highly confident in this assessment based on clear patterns in the data.'
  if (confidence >= 0.75) return 'High - Strong correlation found between current readings and historical patterns.'
  if (confidence >= 0.6) return 'Moderate - Some uncertainty exists. Consider additional manual inspection.'
  if (confidence >= 0.4) return 'Low - Significant uncertainty. Use as a starting point for investigation.'
  return 'Very Low - Limited data or unusual patterns. Manual verification strongly recommended.'
}

export default function AIInsightPanel({
  type = 'analysis',
  title = 'AI Analysis',
  content,
  confidence,
  timestamp,
  actions,
  onExpand,
  className,
  modelType,
  dataSource,
  assetId,
  showModelInfo = false,
  helpText
}: AIInsightPanelProps) {
  const [showDetails, setShowDetails] = useState(false)
  const config = typeConfig[type]
  const Icon = config.icon

  return (
    <div
      className={clsx(
        'border-l-4 rounded-r-lg p-4',
        config.border,
        config.bg,
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Icon size={18} className={config.iconColor} />
          <span className="text-sm font-medium text-surface-200">{title}</span>
          {confidence !== undefined && (
            <button
              className="flex items-center gap-1 text-xs text-surface-500 hover:text-surface-300 transition-colors"
              onClick={() => setShowDetails(!showDetails)}
              title="Click for confidence explanation"
            >
              <span className={clsx(
                'px-1.5 py-0.5 rounded text-xs font-medium',
                confidence >= 0.75 ? 'bg-status-healthy/20 text-status-healthy' :
                confidence >= 0.5 ? 'bg-status-warning/20 text-status-warning' :
                'bg-status-critical/20 text-status-critical'
              )}>
                {Math.round(confidence * 100)}%
              </span>
              <Info size={12} />
            </button>
          )}
          {modelType && (
            <span className="flex items-center gap-1 text-xs text-surface-500 bg-surface-800 px-2 py-0.5 rounded">
              <Brain size={12} />
              {modelInfo[modelType].name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {assetId && (
            <span className="text-xs font-mono text-primary-400">{assetId}</span>
          )}
          {timestamp && (
            <span className="text-xs text-surface-500">{timestamp}</span>
          )}
        </div>
      </div>

      {/* Confidence/Model Details Panel */}
      {showDetails && (
        <div className="mb-3 p-3 bg-surface-900/50 rounded-lg border border-surface-700 space-y-2">
          {confidence !== undefined && (
            <div className="flex items-start gap-2">
              <HelpCircle size={14} className="text-surface-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-surface-300">Confidence Level</p>
                <p className="text-xs text-surface-400">{getConfidenceExplanation(confidence)}</p>
              </div>
            </div>
          )}
          {modelType && showModelInfo && (
            <div className="flex items-start gap-2">
              <Brain size={14} className="text-surface-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-surface-300">{modelInfo[modelType].name}</p>
                <p className="text-xs text-surface-400">{modelInfo[modelType].description}</p>
                <p className="text-xs text-surface-500 mt-1">Typical Accuracy: {modelInfo[modelType].accuracy}</p>
              </div>
            </div>
          )}
          {dataSource && (
            <div className="flex items-start gap-2">
              <Database size={14} className="text-surface-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-surface-300">Data Source</p>
                <p className="text-xs text-surface-400">{dataSource}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help text for technicians */}
      {helpText && (
        <div className="mb-3 flex items-start gap-2 p-2 bg-surface-800/50 rounded text-xs">
          <AlertCircle size={14} className="text-status-warning mt-0.5 flex-shrink-0" />
          <span className="text-surface-400">{helpText}</span>
        </div>
      )}

      {/* Content */}
      <div className="text-sm text-surface-300 leading-relaxed whitespace-pre-wrap">
        {content}
      </div>

      {/* Actions */}
      {(actions || onExpand) && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-700/50">
          <div className="flex items-center gap-2">
            {actions}
          </div>
          {onExpand && (
            <button
              onClick={onExpand}
              className="flex items-center gap-1 text-xs text-surface-400 hover:text-surface-200 transition-colors"
            >
              View Details
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
